import { Typography, Box } from "@mui/material";
import React, { useEffect, useState } from "react";
import { PieChart } from "@mui/x-charts/PieChart";
import { pieArcLabelClasses } from "@mui/x-charts/PieChart";
import ClassifierModel from "../../core/api/ClassifierModel";
import { StatisticsModel } from "../../core/api/statistics/StatisticsModel";

interface ClientState {
  id: string;
  name: string;
}

const COLORS = new Map([
  ["PAST", "#b22222"],
  ["ACTIVE", "#32cd32"],
  ["DEAD", "#191970"],
  ["HIDDEN", "#96cdcd"],
  ["SPECIAL", "#eec900"],
  ["COMATOSE", "#FF00D8"],
  ["OTHER", "#eeb422"],
]);

const ClientCountsChart: React.FC = () => {
  const [chartData, setChartData] = useState<Array<{ id: string; value: number; label: string; color: string }>>([]);
  const [total, setTotal] = useState<number>(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [clientData, clientStates] = await Promise.all([
          StatisticsModel.getClientCounts(),
          ClassifierModel.clientStates(),
        ]);

        const totalClients = StatisticsModel.calculateTotalClients(clientData);
        setTotal(totalClients);

        const stateMap = new Map(clientStates.map((state: ClientState) => [state.id, state.name]));

        const formattedData = clientData.map(item => ({
          id: item.state,
          value: item.count,
          label: (stateMap.get(item.state) || item.state).toString(),
          color: COLORS.get(item.state) ?? "#f5fffa"
        }));

        setChartData(formattedData);
      } catch (error) {
        console.error("Failed to load client statistics:", error);
      }
    };

    fetchData();
  }, []);

  return (
    <Box sx={{ 
      width: '100%', 
      height: '100%', 
      bgcolor: 'background.paper',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        p: 2,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Typography variant="h6">Klientų statistika</Typography>
        <Typography variant="subtitle1">Iš viso: {total}</Typography>
      </Box>
      <Box sx={{ width: '100%', height: 400, p: 1 }}>
        <PieChart
          series={[
            {
              data: chartData,
              highlightScope: { faded: 'global', highlighted: 'item' },
              faded: { innerRadius: 30, additionalRadius: -30, color: 'gray' },
            },
          ]}
          slotProps={{
            legend: {
              direction: 'column',
              position: { vertical: 'top', horizontal: 'right' },
              padding: 0,
              itemMarkWidth: 10,
              itemMarkHeight: 10,
            },
          }}
          sx={{
            [`& .${pieArcLabelClasses.root}`]: {
              fill: 'white',
              fontSize: 14,
            },
          }}
          height={350}
        />
      </Box>
    </Box>
  );
};

export default ClientCountsChart;
